import React, { useState, useEffect, useContext } from 'react';
import { api } from '../../utils/requestInterceptor';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { useRaciAssignments } from '../../hooks/useRaciAssignments';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  PencilIcon,
  ChatBubbleLeftIcon,
  ArrowTopRightOnSquareIcon,
  ExclamationCircleIcon,
  ClockIcon,
  CalendarIcon,
  BoltIcon,
  ListBulletIcon,
  TableCellsIcon,
  ArrowsUpDownIcon,
  ViewColumnsIcon,
  ArrowLongRightIcon,
  UserIcon
} from '@heroicons/react/24/outline';

function MyWork() {
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const { getBatchAssignments } = useRaciAssignments();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assignedWork, setAssignedWork] = useState([]);
  const [expandedProjects, setExpandedProjects] = useState({});
  const [expandedPhases, setExpandedPhases] = useState({});
  const [filter, setFilter] = useState('all'); // all, overdue, thisWeek, recentlyUpdated
  const [viewMode, setViewMode] = useState('hierarchical'); // hierarchical, table, or cards
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [flattenedTasks, setFlattenedTasks] = useState([]);
  const [sortOrder, setSortOrder] = useState('dueDate'); // Default sort by due date for cards
  const [projectAssignments, setProjectAssignments] = useState({});
  
  // Helper function to check if status is active (not completed or canceled)
  const isActiveStatus = (status) => {
    if (!status) return true; // If no status, consider it active
    const lowercaseStatus = status.toLowerCase();
    return lowercaseStatus !== 'completed' && lowercaseStatus !== 'cancelled' && lowercaseStatus !== 'canceled';
  };
  
  // Fetch assignments using our batch approach with the new RACI structure
  useEffect(() => {
    const fetchAssignedWork = async () => {
      if (!currentUser?.id) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Get all projects first
        const projectsResponse = await api.get('/projects');
        const allProjects = projectsResponse.data.projects || [];
        
        // Get all project IDs
        const projectIds = allProjects.map(project => project.id);
        
        // Fetch all assignments for all projects in a single batch call
        const assignments = await getBatchAssignments('project', projectIds);
        
        // Store the assignments in state
        setProjectAssignments(assignments);
        
        // Process each project
        const assignedProjects = [];
        
        for (const project of allProjects) {
          // Skip if no assignments data
          if (!assignments[project.id]) continue;
          
          // Fix client mapping - ensure client data is properly structured
          let clientData = null;
          if (project.client_id) {
            clientData = {
              id: project.client_id,
              name: project.client_name || 'Unknown Client'
            };
          }
          
          // We no longer check for responsible at project level
          // Instead, we focus only on accountable, consulted, and informed
          const isAccountableForProject = assignments[project.id].project?.assignments?.A?.some(
            a => a.user_id === currentUser.id
          );
          
          const isConsultedForProject = assignments[project.id].project?.assignments?.C?.some(
            a => a.user_id === currentUser.id
          );
          
          const isInformedForProject = assignments[project.id].project?.assignments?.I?.some(
            a => a.user_id === currentUser.id
          );
          
          // Collect all phases where the user is responsible (directly or via steps)
          const phasesWithAssignments = [];
          
          // Process all phases
          for (const phase of project.phases) {
            const phaseAssignments = assignments[project.id]?.phases?.[phase.id]?.assignments;
            
            // Check if directly responsible for the phase
            const isDirectlyResponsibleForPhase = phaseAssignments?.R?.some(
              a => a.user_id === currentUser.id
            );
            
            // Find steps where the user is responsible
            const responsibleSteps = [];
            
            for (const step of phase.steps) {
              const stepAssignments = assignments[project.id]?.steps?.[step.id]?.assignments;
              
              if (stepAssignments?.R?.some(a => a.user_id === currentUser.id)) {
                responsibleSteps.push({
                  ...step,
                  isResponsible: true
                });
              }
            }
            
            // Include phase if user is directly responsible for it or for any of its steps
            if (isDirectlyResponsibleForPhase || responsibleSteps.length > 0) {
              phasesWithAssignments.push({
                ...phase,
                isDirectlyResponsible: isDirectlyResponsibleForPhase,
                assignedSteps: responsibleSteps
              });
            }
          }
          
          // Include project if user has any relevant assignment
          if (
            isAccountableForProject || 
            isConsultedForProject || 
            isInformedForProject || 
            phasesWithAssignments.length > 0
          ) {
            assignedProjects.push({
              ...project,
              client: clientData, // Set properly structured client data
              isAccountable: isAccountableForProject,
              isConsulted: isConsultedForProject,
              isInformed: isInformedForProject,
              assignedPhases: phasesWithAssignments
            });
          }
        }
        
        setAssignedWork(assignedProjects);
      } catch (error) {
        console.error('Error fetching assigned work:', error);
        setError('Failed to load your assigned work');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAssignedWork();
  }, [currentUser?.id, getBatchAssignments]);
  
  // Toggle project expansion
  const toggleProject = (projectId) => {
    setExpandedProjects(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };
  
  // Toggle phase expansion
  const togglePhase = (phaseId) => {
    setExpandedPhases(prev => ({
      ...prev,
      [phaseId]: !prev[phaseId]
    }));
  };
  
  // Navigate to project/phase/step
  const navigateTo = (type, id) => {
    switch (type) {
      case 'project':
        navigate(`/projects/${id}`);
        break;
      case 'phase':
        navigate(`/phases/${id}`);
        break;
      case 'step':
        navigate(`/steps/${id}`);
        break;
      case 'client':
        navigate(`/clients/${id}`);
        break;
      default:
        break;
    }
  };
  
  // Update status
  const updateStatus = async (type, id, newStatus) => {
    try {
      if (type === 'project') {
        await api.put(`/projects/${id}`, { status: newStatus });
      } else if (type === 'phase') {
        await api.put(`/phases/${id}`, { status: newStatus });
      } else if (type === 'step') {
        await api.put(`/steps/${id}`, { status: newStatus });
      }
      
      // Refresh the data
      // For simplicity, we'll just refetch everything
      // In a real app, you might want to update just the changed item
      
      toast.success(`Status updated successfully`);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };
  
  // Add note
  const addNote = async (type, id) => {
    // Navigate to the entity details page with the notes tab active
    switch (type) {
      case 'project':
        navigate(`/projects/${id}?tab=notes`);
        break;
      case 'phase':
        navigate(`/phases/${id}?tab=notes`);
        break;
      case 'step':
        navigate(`/steps/${id}?tab=notes`);
        break;
      default:
        break;
    }
  };
  
  // Helper to get due date status
  const getDueDateStatus = (dueDate) => {
    if (!dueDate) return { color: 'gray', icon: null, text: 'No due date' };
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const diffTime = new Date(dueDate).getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { 
        color: 'red', 
        icon: <ExclamationCircleIcon className="h-5 w-5 text-red-500" />, 
        text: `Overdue by ${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''}` 
      };
    } else if (diffDays <= 7) {
      return { 
        color: 'orange', 
        icon: <ClockIcon className="h-5 w-5 text-orange-500" />, 
        text: diffDays === 0 ? 'Due today' : `Due in ${diffDays} day${diffDays !== 1 ? 's' : ''}` 
      };
    } else {
      return { 
        color: 'green', 
        icon: <CalendarIcon className="h-5 w-5 text-green-500" />, 
        text: `Due in ${diffDays} days` 
      };
    }
  };
  
  // Filter assignments based on selected filter
  const getFilteredWork = () => {
    // First filter out completed/canceled projects entirely
    const activeProjects = assignedWork.filter(project => isActiveStatus(project.status));
    
    // Then process date filtering if needed
    let filteredByDueDate = activeProjects;
    
    if (filter !== 'all') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const oneWeek = new Date(today);
      oneWeek.setDate(oneWeek.getDate() + 7);
      
      // Helper to check if the item matches the date filter
      const matchesDateFilter = (dueDate) => {
        if (!dueDate) return false;
        
        const itemDate = new Date(dueDate);
        
        if (filter === 'overdue') {
          return itemDate < today;
        } else if (filter === 'thisWeek') {
          return itemDate >= today && itemDate <= oneWeek;
        }
        
        return false;
      };
      
      // Filter projects
      filteredByDueDate = activeProjects
        .map(project => {
          const projectMatches = matchesDateFilter(project.due_date);
          
          // Filter phases
          const filteredPhases = project.assignedPhases
            .map(phase => {
              const phaseMatches = matchesDateFilter(phase.due_date);
              
              // Filter steps
              const filteredSteps = phase.assignedSteps.filter(step => 
                matchesDateFilter(step.due_date)
              );
              
              return {
                ...phase,
                assignedSteps: filteredSteps,
                hasMatchingItems: phaseMatches || filteredSteps.length > 0
              };
            })
            .filter(phase => phase.hasMatchingItems);
          
          return {
            ...project,
            assignedPhases: filteredPhases,
            hasMatchingItems: projectMatches || filteredPhases.length > 0
          };
        })
        .filter(project => project.hasMatchingItems);
    }
    
    // Then filter out completed/canceled items at phase and step levels
    return filteredByDueDate
      .map(project => {
        // Filter phases by status
        const activePhases = project.assignedPhases
          .filter(phase => isActiveStatus(phase.status)) // First filter out inactive phases
          .map(phase => {
            // Filter steps by status
            const activeSteps = phase.assignedSteps.filter(step => 
              isActiveStatus(step.status)
            );
            
            return {
              ...phase,
              assignedSteps: activeSteps,
              hasActiveItems: activeSteps.length > 0
            };
          })
          .filter(phase => phase.hasActiveItems);
        
        return {
          ...project,
          assignedPhases: activePhases,
          hasActiveItems: activePhases.length > 0
        };
      })
      .filter(project => project.hasActiveItems);
  };
  
  // Get filtered work
  const filteredWork = getFilteredWork();
  
  // Get sorted work for hierarchical and table views
  const sortedWork = [...filteredWork].sort((a, b) => {
    // Sort by due date (null dates at the end)
    if (!a.due_date && !b.due_date) return 0;
    if (!a.due_date) return 1;
    if (!b.due_date) return -1;
    return new Date(a.due_date) - new Date(b.due_date);
  });
  
  // Modify the rendering of project items to show RACI role badges
  const renderProjectRoleBadges = (project) => {
    const badges = [];
    
    if (project.isAccountable) {
      badges.push(
        <span key="A" className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
          A
        </span>
      );
    }
    
    if (project.isConsulted) {
      badges.push(
        <span key="C" className="ml-1 px-2 py-0.5 text-xs bg-purple-100 text-purple-800 rounded-full">
          C
        </span>
      );
    }
    
    if (project.isInformed) {
      badges.push(
        <span key="I" className="ml-1 px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded-full">
          I
        </span>
      );
    }
    
    // Always show the work items badge if they have phases assigned
    if (project.assignedPhases.length > 0) {
      badges.push(
        <span key="phases" className="ml-1 px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full flex items-center">
          <BoltIcon className="h-3 w-3 mr-0.5" />
          Work Items
        </span>
      );
    }
    
    return badges.length > 0 ? (
      <div className="flex flex-wrap mt-1">{badges}</div>
    ) : null;
  };
  
  // Badge rendering for phases
  const renderPhaseBadges = (phase) => {
    return (
      <div className="flex items-center">
        {phase.isDirectlyResponsible && (
          <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full">
            R
          </span>
        )}
        
        {phase.assignedSteps.length > 0 && (
          <span className="ml-1 px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full flex items-center">
            <BoltIcon className="h-3 w-3 mr-0.5" />
            {phase.assignedSteps.length} Step{phase.assignedSteps.length !== 1 ? 's' : ''}
          </span>
        )}
        
        {getDueDateStatus(phase.due_date).icon && (
          <div className="ml-2 flex items-center">
            {getDueDateStatus(phase.due_date).icon}
            <span className="ml-1 text-xs">
              {getDueDateStatus(phase.due_date).text}
            </span>
          </div>
        )}
      </div>
    );
  };

  // Badge rendering for steps
  const renderStepBadges = (step) => {
    return (
      <div className="flex items-center">
        <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full">
          R
        </span>
        
        {getDueDateStatus(step.due_date).icon && (
          <div className="ml-2 flex items-center">
            {getDueDateStatus(step.due_date).icon}
            <span className="ml-1 text-xs">
              {getDueDateStatus(step.due_date).text}
            </span>
          </div>
        )}
      </div>
    );
  };

  // Create flattened view of tasks for table view
  useEffect(() => {
    if (assignedWork.length === 0) {
      setFlattenedTasks([]);
      return;
    }

    const tasks = [];

    // Use filtered work instead of all assigned work
    const filteredWork = getFilteredWork();

    // Add project level tasks
    filteredWork.forEach(project => {
      // Get proper client name
      const clientName = project.client?.name || project.client_name || 'No client';
      
      tasks.push({
        id: `project-${project.id}`,
        type: 'project',
        entityId: project.id,
        name: project.name,
        status: project.status || 'Not set',
        dueDate: project.due_date,
        client: clientName,
        dueDateStatus: getDueDateStatus(project.due_date),
        parentName: null,
        role: project.isAccountable ? 'A' : project.isConsulted ? 'C' : project.isInformed ? 'I' : 'R',
        level: 0, // Add level property for indentation
        projectId: project.id // Add reference to parent project ID
      });

      // Add phase level tasks
      project.assignedPhases.forEach(phase => {
        tasks.push({
          id: `phase-${phase.id}`,
          type: 'phase',
          entityId: phase.id,
          name: phase.name,
          status: phase.status || 'Not set',
          dueDate: phase.due_date,
          client: clientName,
          dueDateStatus: getDueDateStatus(phase.due_date),
          parentName: project.name,
          parentId: project.id,
          role: phase.isDirectlyResponsible ? 'R' : '',
          level: 1, // Add level property for indentation
          projectId: project.id // Add reference to parent project ID
        });

        // Add step level tasks
        phase.assignedSteps.forEach(step => {
          tasks.push({
            id: `step-${step.id}`,
            type: 'step',
            entityId: step.id,
            name: step.name,
            status: step.status || 'Not set',
            dueDate: step.due_date,
            client: clientName,
            dueDateStatus: getDueDateStatus(step.due_date),
            parentName: phase.name,
            parentId: phase.id,
            grandparentName: project.name,
            grandparentId: project.id,
            role: 'R',
            level: 2, // Add level property for indentation
            projectId: project.id, // Add reference to parent project ID
            phaseId: phase.id // Add reference to parent phase ID
          });
        });
      });
    });

    setFlattenedTasks(tasks);
  }, [assignedWork, filter]); // Add filter to dependencies

  // Sorting function for table view
  const sortTable = (key) => {
    let direction = 'ascending';
    
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    
    setSortConfig({ key, direction });
  };

  // Get sorted tasks for table
  const getSortedTasks = () => {
    let tasksToSort = [...flattenedTasks];
    
    if (sortConfig.key) {
      // If we're sorting by key, use regular sorting logic
      tasksToSort = tasksToSort.sort((a, b) => {
        // Handle null values
        if (a[sortConfig.key] === null) return sortConfig.direction === 'ascending' ? 1 : -1;
        if (b[sortConfig.key] === null) return sortConfig.direction === 'ascending' ? -1 : 1;
        
        // Special handling for due date
        if (sortConfig.key === 'dueDate') {
          if (!a.dueDate) return sortConfig.direction === 'ascending' ? 1 : -1;
          if (!b.dueDate) return sortConfig.direction === 'ascending' ? -1 : 1;
          
          const dateA = new Date(a.dueDate).getTime();
          const dateB = new Date(b.dueDate).getTime();
          
          return sortConfig.direction === 'ascending' ? dateA - dateB : dateB - dateA;
        }
        
        // Regular string comparison
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    } else {
      // Restructure the tasks to enforce proper hierarchy
      const projectMap = {};
      const hierarchicalTasks = [];
      
      // Group all items by their project
      tasksToSort.forEach(task => {
        if (!projectMap[task.projectId]) {
          projectMap[task.projectId] = {
            project: null,
            phases: {},
            steps: []
          };
        }
        
        if (task.type === 'project') {
          projectMap[task.projectId].project = task;
        } else if (task.type === 'phase') {
          if (!projectMap[task.projectId].phases[task.entityId]) {
            projectMap[task.projectId].phases[task.entityId] = {
              phase: task,
              steps: []
            };
          } else {
            projectMap[task.projectId].phases[task.entityId].phase = task;
          }
        } else if (task.type === 'step') {
          // Ensure the phase exists in our map
          if (!projectMap[task.projectId].phases[task.phaseId]) {
            projectMap[task.projectId].phases[task.phaseId] = {
              phase: null,
              steps: []
            };
          }
          
          projectMap[task.projectId].phases[task.phaseId].steps.push(task);
        }
      });
      
      // Build the hierarchical list
      Object.keys(projectMap).forEach(projectId => {
        const projectData = projectMap[projectId];
        
        // Add project
        if (projectData.project) {
          hierarchicalTasks.push(projectData.project);
          
          // Add phases for this project
          Object.keys(projectData.phases).forEach(phaseId => {
            const phaseData = projectData.phases[phaseId];
            
            // Add phase if it exists
            if (phaseData.phase) {
              hierarchicalTasks.push(phaseData.phase);
            }
            
            // Add steps for this phase
            phaseData.steps.forEach(step => {
              hierarchicalTasks.push(step);
            });
          });
        }
      });
      
      tasksToSort = hierarchicalTasks;
    }
    
    return tasksToSort;
  };
  
  // Generate background color for grouping
  const getRowBackgroundColor = (task, index, tasks) => {
    // If we're not sorting, apply alternating group colors
    if (!sortConfig.key) {
      // Find project groups
      let projectIndex = 0;
      for (let i = 0; i < tasks.length; i++) {
        if (tasks[i].type === 'project') {
          if (tasks[i].projectId === task.projectId) {
            projectIndex = Math.floor(i / 3) % 2; // Alternating colors every 2-3 projects
            break;
          }
        }
      }
      
      return projectIndex === 0 ? 'bg-white' : 'bg-gray-50';
    }
    
    // If we are sorting, use alternating rows
    return index % 2 === 0 ? 'bg-white' : 'bg-gray-50';
  };
  
  // Calculate project progress
  const calculateProjectProgress = (project) => {
    if (!project.phases || project.phases.length === 0) return 0;
    
    let completedCount = 0;
    let totalCount = 0;
    
    project.phases.forEach(phase => {
      if (phase.status && phase.status.toLowerCase() === 'completed') {
        completedCount++;
      }
      totalCount++;
      
      if (phase.steps && phase.steps.length > 0) {
        phase.steps.forEach(step => {
          if (step.status && step.status.toLowerCase() === 'completed') {
            completedCount++;
          }
          totalCount++;
        });
      }
    });
    
    return totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  };
  
  // Find next phase and step that needs completion
  const findNextIncompleteItem = (project) => {
    if (!project.phases || project.phases.length === 0) {
      return { phase: null, step: null };
    }
    
    // Sort phases by order if available
    const sortedPhases = [...project.phases].sort((a, b) => {
      if (a.order !== undefined && b.order !== undefined) {
        return a.order - b.order;
      }
      return 0;
    });
    
    // Find first incomplete phase
    for (const phase of sortedPhases) {
      if (!phase.status || phase.status.toLowerCase() !== 'completed') {
        // If phase has steps, find first incomplete step
        if (phase.steps && phase.steps.length > 0) {
          const sortedSteps = [...phase.steps].sort((a, b) => {
            if (a.order !== undefined && b.order !== undefined) {
              return a.order - b.order;
            }
            return 0;
          });
          
          for (const step of sortedSteps) {
            if (!step.status || step.status.toLowerCase() !== 'completed') {
              return { phase, step };
            }
          }
        }
        return { phase, step: null };
      }
    }
    
    return { phase: null, step: null };
  };
  
  // Find accountable person(s) for a project
  const findAccountablePerson = (project, projectAssignments) => {
    if (!projectAssignments || !projectAssignments[project.id]) return [];
    
    // Get all accountable users at project level
    const accountableUsers = projectAssignments[project.id].project?.assignments?.A || [];
    if (accountableUsers.length > 0) {
      return accountableUsers.map(user => user.user_name || 'Unnamed user');
    }
    
    // If no accountable users at project level, look at phases
    const phaseAccountable = [];
    for (const phase of project.phases || []) {
      const phaseAssignments = projectAssignments[project.id]?.phases?.[phase.id]?.assignments;
      const phaseAccountableUsers = phaseAssignments?.A || [];
      
      phaseAccountableUsers.forEach(user => {
        if (user.user_name && !phaseAccountable.includes(user.user_name)) {
          phaseAccountable.push(user.user_name);
        }
      });
    }
    
    if (phaseAccountable.length > 0) {
      return phaseAccountable;
    }
    
    return [];
  };
  
  // Get sorted projects for card view
  const getSortedProjects = () => {
    // First filter out non-active projects
    let projectsToSort = filteredWork.filter(project => isActiveStatus(project.status));
    
    if (sortOrder === 'dueDate') {
      projectsToSort.sort((a, b) => {
        if (!a.due_date && !b.due_date) return 0;
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return new Date(a.due_date) - new Date(b.due_date);
      });
    } else if (sortOrder === 'name') {
      projectsToSort.sort((a, b) => {
        return a.name.localeCompare(b.name);
      });
    } else if (sortOrder === 'progress') {
      projectsToSort.sort((a, b) => {
        const progressA = calculateProjectProgress(a);
        const progressB = calculateProjectProgress(b);
        return progressB - progressA; // Sort by highest progress first
      });
    }
    
    return projectsToSort;
  };
  
  if (loading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="sm:flex sm:items-center mb-6">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold text-gray-900">My Work</h1>
            <p className="mt-2 text-sm text-gray-700">
              View all projects, phases, and steps assigned to you
            </p>
          </div>
        </div>
        
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="sm:flex sm:items-center mb-6">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold text-gray-900">My Work</h1>
            <p className="mt-2 text-sm text-gray-700">
              View all projects, phases, and steps assigned to you
            </p>
          </div>
        </div>
        
        <div className="bg-red-50 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="max-w-full">
      <div className="py-4">
        <h1 className="text-2xl font-semibold text-gray-900">My Work</h1>
        <p className="mt-1 text-sm text-gray-600">
          View all projects, phases, and steps assigned to you
        </p>
      </div>
      
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{error}</h3>
              <button 
                onClick={() => window.location.reload()}
                className="mt-2 text-sm text-red-800 underline"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex justify-between items-center mb-4">
            <div className="flex space-x-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  filter === 'all' 
                    ? 'bg-indigo-100 text-indigo-700' 
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                All Work
              </button>
              <button
                onClick={() => setFilter('overdue')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  filter === 'overdue' 
                    ? 'bg-red-100 text-red-700' 
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Overdue
              </button>
              <button
                onClick={() => setFilter('thisWeek')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  filter === 'thisWeek' 
                    ? 'bg-orange-100 text-orange-700' 
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Due This Week
              </button>
            </div>
            
            {/* View toggle */}
            <div className="flex items-center bg-white rounded-md border border-gray-300">
              <button
                onClick={() => setViewMode('hierarchical')}
                className={`px-3 py-2 flex items-center text-sm ${
                  viewMode === 'hierarchical' 
                    ? 'bg-indigo-50 text-indigo-600 font-medium' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <ListBulletIcon className="h-4 w-4 mr-1" />
                Tree View
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-2 flex items-center text-sm ${
                  viewMode === 'table' 
                    ? 'bg-indigo-50 text-indigo-600 font-medium' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <TableCellsIcon className="h-4 w-4 mr-1" />
                Table View
              </button>
              <button
                onClick={() => setViewMode('cards')}
                className={`px-3 py-2 flex items-center text-sm ${
                  viewMode === 'cards' 
                    ? 'bg-indigo-50 text-indigo-600 font-medium' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <ViewColumnsIcon className="h-4 w-4 mr-1" />
                Cards View
              </button>
            </div>
          </div>
          
          {/* Card View Sorting Options */}
          {viewMode === 'cards' && (
            <div className="flex justify-end mb-4">
              <div className="inline-flex rounded-md shadow-sm">
                <button
                  onClick={() => setSortOrder('dueDate')}
                  className={`px-3 py-1.5 text-xs font-medium border ${
                    sortOrder === 'dueDate' 
                      ? 'bg-indigo-50 border-indigo-300 text-indigo-700' 
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  } rounded-l-md`}
                >
                  Due Date
                </button>
                <button
                  onClick={() => setSortOrder('name')}
                  className={`px-3 py-1.5 text-xs font-medium border-t border-b ${
                    sortOrder === 'name' 
                      ? 'bg-indigo-50 border-indigo-300 text-indigo-700' 
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Project Name
                </button>
                <button
                  onClick={() => setSortOrder('progress')}
                  className={`px-3 py-1.5 text-xs font-medium border ${
                    sortOrder === 'progress' 
                      ? 'bg-indigo-50 border-indigo-300 text-indigo-700' 
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  } rounded-r-md`}
                >
                  Progress
                </button>
              </div>
            </div>
          )}
          
          {/* Hierarchical View */}
          {viewMode === 'hierarchical' && (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              {sortedWork.length === 0 ? (
                <div className="px-4 py-12 text-center">
                  <p className="text-gray-500 mt-2">No assigned work found for the selected filter.</p>
                  {filter !== 'all' && (
                    <button
                      onClick={() => setFilter('all')}
                      className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      View All Work
                    </button>
                  )}
                </div>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {sortedWork.map(project => (
                    <li key={project.id} className="block hover:bg-gray-50">
                      <div className="px-4 py-4 sm:px-6">
                        <div className="flex items-center justify-between">
                          <button 
                            onClick={() => toggleProject(project.id)}
                            className="flex items-center text-left"
                          >
                            {expandedProjects[project.id] ? (
                              <ChevronDownIcon className="h-5 w-5 text-gray-400 mr-2" />
                            ) : (
                              <ChevronRightIcon className="h-5 w-5 text-gray-400 mr-2" />
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center">
                                <p className="text-sm font-medium text-indigo-600 truncate">
                                  {project.name}
                                </p>
                                <div className="ml-2 flex-shrink-0 flex">
                                  <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                                    {project.client?.name || 'No client'}
                                  </p>
                                </div>
                              </div>
                              <div className="mt-1 sm:flex sm:justify-between">
                                <div className="flex items-center text-sm text-gray-500">
                                  <p>Status: {project.status || 'Not set'}</p>
                                </div>
                                <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                                  {renderProjectRoleBadges(project)}
                                </div>
                              </div>
                              {/* Restore due date display */}
                              <div className="mt-1 flex items-center text-sm text-gray-500">
                                {getDueDateStatus(project.due_date).icon}
                                <p className="ml-1">
                                  {getDueDateStatus(project.due_date).text}
                                </p>
                              </div>
                            </div>
                          </button>
                          <div className="ml-2 flex-shrink-0 flex">
                            <button
                              onClick={() => addNote('project', project.id)}
                              className="ml-2 text-gray-400 hover:text-gray-500"
                              title="Add note"
                            >
                              <ChatBubbleLeftIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => navigateTo('project', project.id)}
                              className="ml-2 text-gray-400 hover:text-gray-500"
                              title="View project"
                            >
                              <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                        
                        {expandedProjects[project.id] && (
                          <div className="ml-7 mt-3">
                            {project.assignedPhases.length === 0 ? (
                              <p className="text-sm text-gray-500 italic">No phases assigned to you in this project</p>
                            ) : (
                              <ul className="border-l-2 border-gray-200 pl-3 space-y-3">
                                {project.assignedPhases.map(phase => (
                                  <li key={phase.id}>
                                    <div className="flex items-center justify-between">
                                      <button 
                                        onClick={() => togglePhase(phase.id)}
                                        className="flex items-center text-left"
                                      >
                                        {expandedPhases[phase.id] ? (
                                          <ChevronDownIcon className="h-4 w-4 text-gray-400 mr-2" />
                                        ) : (
                                          <ChevronRightIcon className="h-4 w-4 text-gray-400 mr-2" />
                                        )}
                                        <div>
                                          <div className="flex items-center">
                                            <p className="text-sm font-medium text-gray-700 truncate">
                                              {phase.name}
                                            </p>
                                          </div>
                                          <div className="mt-1 sm:flex sm:justify-between">
                                            <div className="flex items-center text-sm text-gray-500">
                                              <p>Status: {phase.status || 'Not set'}</p>
                                            </div>
                                            <div className="mt-1 flex items-center text-xs text-gray-500 sm:mt-0">
                                              {renderPhaseBadges(phase)}
                                            </div>
                                          </div>
                                        </div>
                                      </button>
                                      <div className="ml-2 flex-shrink-0 flex">
                                        <button
                                          onClick={() => addNote('phase', phase.id)}
                                          className="ml-2 text-gray-400 hover:text-gray-500"
                                          title="Add note"
                                        >
                                          <ChatBubbleLeftIcon className="h-4 w-4" />
                                        </button>
                                        <button
                                          onClick={() => navigateTo('phase', phase.id)}
                                          className="ml-2 text-gray-400 hover:text-gray-500"
                                          title="View phase"
                                        >
                                          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                                        </button>
                                      </div>
                                    </div>
                                    
                                    {expandedPhases[phase.id] && (
                                      <div className="ml-6 mt-2">
                                        {phase.assignedSteps.length === 0 ? (
                                          <p className="text-sm text-gray-500 italic">No steps assigned to you in this phase</p>
                                        ) : (
                                          <ul className="border-l-2 border-gray-100 pl-3 space-y-2">
                                            {phase.assignedSteps.map(step => (
                                              <li key={step.id} className="py-1">
                                                <div className="flex items-center justify-between">
                                                  <div className="flex items-center">
                                                    <div className="mx-2 h-1.5 w-1.5 rounded-full bg-gray-400"></div>
                                                    <div>
                                                      <p className="text-sm font-medium text-gray-600 truncate">
                                                        {step.name}
                                                      </p>
                                                      <div className="flex items-center space-x-2 text-xs text-gray-500 mt-0.5">
                                                        <span>Status: {step.status || 'Not set'}</span>
                                                        <span className="flex items-center">
                                                          {renderStepBadges(step)}
                                                        </span>
                                                      </div>
                                                    </div>
                                                  </div>
                                                  <div className="ml-2 flex-shrink-0 flex">
                                                    <button
                                                      onClick={() => addNote('step', step.id)}
                                                      className="ml-2 text-gray-400 hover:text-gray-500"
                                                      title="Add note"
                                                    >
                                                      <ChatBubbleLeftIcon className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                      onClick={() => navigateTo('step', step.id)}
                                                      className="ml-2 text-gray-400 hover:text-gray-500"
                                                      title="View step"
                                                    >
                                                      <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                                                    </button>
                                                  </div>
                                                </div>
                                              </li>
                                            ))}
                                          </ul>
                                        )}
                                      </div>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          
          {/* Table View */}
          {viewMode === 'table' && (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              {flattenedTasks.length === 0 ? (
                <div className="px-4 py-12 text-center">
                  <p className="text-gray-500 mt-2">No assigned work found for the selected filter.</p>
                  {filter !== 'all' && (
                    <button
                      onClick={() => setFilter('all')}
                      className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      View All Work
                    </button>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th 
                          scope="col" 
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => sortTable('type')}
                        >
                          <div className="flex items-center">
                            Type
                            {sortConfig.key === 'type' && (
                              <ArrowsUpDownIcon className="ml-1 h-4 w-4 text-gray-400" />
                            )}
                          </div>
                        </th>
                        <th 
                          scope="col" 
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => sortTable('name')}
                        >
                          <div className="flex items-center">
                            Name
                            {sortConfig.key === 'name' && (
                              <ArrowsUpDownIcon className="ml-1 h-4 w-4 text-gray-400" />
                            )}
                          </div>
                        </th>
                        <th 
                          scope="col" 
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => sortTable('status')}
                        >
                          <div className="flex items-center">
                            Status
                            {sortConfig.key === 'status' && (
                              <ArrowsUpDownIcon className="ml-1 h-4 w-4 text-gray-400" />
                            )}
                          </div>
                        </th>
                        <th 
                          scope="col" 
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => sortTable('dueDate')}
                        >
                          <div className="flex items-center">
                            Due Date
                            {sortConfig.key === 'dueDate' && (
                              <ArrowsUpDownIcon className="ml-1 h-4 w-4 text-gray-400" />
                            )}
                          </div>
                        </th>
                        <th 
                          scope="col" 
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => sortTable('client')}
                        >
                          <div className="flex items-center">
                            Client
                            {sortConfig.key === 'client' && (
                              <ArrowsUpDownIcon className="ml-1 h-4 w-4 text-gray-400" />
                            )}
                          </div>
                        </th>
                        <th 
                          scope="col" 
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => sortTable('role')}
                        >
                          <div className="flex items-center">
                            Role
                            {sortConfig.key === 'role' && (
                              <ArrowsUpDownIcon className="ml-1 h-4 w-4 text-gray-400" />
                            )}
                          </div>
                        </th>
                        <th scope="col" className="relative px-6 py-3">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {getSortedTasks().map((task, index, tasks) => {
                        // Determine if this is the first task in a project group
                        const isFirstInProjectGroup = index === 0 || 
                          tasks[index-1].projectId !== task.projectId;
                          
                        // Determine if this is the first task in a phase group
                        const isFirstInPhaseGroup = task.level === 2 && (
                          index === 0 || 
                          tasks[index-1].level !== 2 || 
                          tasks[index-1].phaseId !== task.phaseId
                        );
                          
                        return (
                          <tr 
                            key={task.id} 
                            className={`${getRowBackgroundColor(task, index, tasks)} hover:bg-blue-50 ${
                              isFirstInProjectGroup ? 'border-t-2 border-indigo-200' : ''
                            } ${isFirstInPhaseGroup ? 'border-t border-purple-100' : ''} ${
                              task.level === 0 ? 'font-medium' : ''
                            }`}
                          >
                            <td className="px-6 py-3 whitespace-nowrap text-sm font-medium">
                              <div className="flex items-center">
                                {/* Add indentation based on level */}
                                {task.level > 0 && (
                                  <div 
                                    className="flex-shrink-0"
                                    style={{ width: `${task.level * 20}px` }}
                                  ></div>
                                )}
                                <span className={`px-2 py-1 rounded-md text-xs font-medium ${
                                  task.type === 'project' 
                                    ? 'bg-blue-100 text-blue-800' 
                                    : task.type === 'phase'
                                      ? 'bg-purple-100 text-purple-800'
                                      : 'bg-green-100 text-green-800'
                                }`}>
                                  {task.type.charAt(0).toUpperCase() + task.type.slice(1)}
                                </span>
                                {task.level === 0 && (
                                  <span className="ml-2 px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-600">
                                    {task.client}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-sm font-medium">
                              <div className={`flex items-center`}>
                                {/* Add indentation based on level */}
                                {task.level > 0 && (
                                  <div 
                                    className="flex-shrink-0"
                                    style={{ width: `${task.level * 20}px` }}
                                  ></div>
                                )}
                                
                                {/* Add visual indicators for hierarchy */}
                                {task.level === 1 && (
                                  <div className="w-1 h-3 bg-purple-400 rounded-sm mr-2"></div>
                                )}
                                {task.level === 2 && (
                                  <div className="w-1 h-3 bg-green-400 rounded-sm mr-2"></div>
                                )}
                                
                                <span className={`${
                                  task.level === 0 ? 'text-indigo-700 font-medium' : 
                                  task.level === 1 ? 'text-purple-700' : 
                                  'text-green-700'
                                }`}>
                                  {task.name}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                              {task.status}
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                              <div className="flex items-center">
                                {task.dueDateStatus.icon}
                                <span className="ml-1">{task.dueDateStatus.text}</span>
                              </div>
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                              {task.client}
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                task.role === 'R' ? 'bg-blue-100 text-blue-800' :
                                task.role === 'A' ? 'bg-green-100 text-green-800' :
                                task.role === 'C' ? 'bg-purple-100 text-purple-800' :
                                task.role === 'I' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {task.role || '-'}
                              </span>
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex justify-end space-x-2">
                                <button
                                  onClick={() => addNote(task.type, task.entityId)}
                                  className="text-gray-400 hover:text-gray-500"
                                  title="Add note"
                                >
                                  <ChatBubbleLeftIcon className="h-5 w-5" />
                                </button>
                                <button
                                  onClick={() => navigateTo(task.type, task.entityId)}
                                  className="text-gray-400 hover:text-gray-500"
                                  title={`View ${task.type}`}
                                >
                                  <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          
          {/* Card View */}
          {viewMode === 'cards' && (
            <div>
              {filteredWork.length === 0 ? (
                <div className="px-4 py-12 text-center bg-white shadow rounded-md">
                  <p className="text-gray-500 mt-2">No assigned work found for the selected filter.</p>
                  {filter !== 'all' && (
                    <button
                      onClick={() => setFilter('all')}
                      className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      View All Work
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {getSortedProjects().map(project => {
                    const progress = calculateProjectProgress(project);
                    const { phase: nextPhase, step: nextStep } = findNextIncompleteItem(project);
                    const accountablePersons = findAccountablePerson(project, projectAssignments);
                    const dueDateStatus = getDueDateStatus(project.due_date);
                    
                    return (
                      <div key={project.id} className="bg-white overflow-hidden shadow rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                          <h3 
                            className="text-lg font-medium text-indigo-600 truncate cursor-pointer hover:underline"
                            onClick={() => navigateTo('project', project.id)}
                          >
                            {project.name}
                          </h3>
                          {project.client && project.client.id ? (
                            <p 
                              className="mt-1 text-sm text-gray-500 cursor-pointer hover:text-indigo-500 hover:underline"
                              onClick={() => navigateTo('client', project.client.id)}
                            >
                              {project.client.name || 'No client'}
                            </p>
                          ) : (
                            <p className="mt-1 text-sm text-gray-500">
                              {project.client?.name || 'No client'}
                            </p>
                          )}
                        </div>
                        <div className="px-4 py-4 sm:px-6">
                          {/* Progress Bar */}
                          <div className="mb-4">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-gray-700">Progress</span>
                              <span className="text-sm font-medium text-gray-700">{progress}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-indigo-600 h-2 rounded-full" 
                                style={{ width: `${progress}%` }}
                              ></div>
                            </div>
                          </div>
                          
                          {/* Next Items */}
                          <div className="mb-4">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Next Up</h4>
                            {nextPhase ? (
                              <div>
                                <div className="flex items-center">
                                  <span className="flex h-6 w-6 rounded-full items-center justify-center bg-purple-100 text-purple-800 text-xs font-medium">P</span>
                                  <span className="ml-2 text-sm text-gray-600">{nextPhase.name}</span>
                                </div>
                                {nextStep && (
                                  <div className="ml-4 mt-1.5 flex items-center">
                                    <ArrowLongRightIcon className="h-3 w-3 text-gray-400 mr-1" />
                                    <span className="flex h-5 w-5 rounded-full items-center justify-center bg-green-100 text-green-800 text-xs font-medium">S</span>
                                    <span className="ml-2 text-sm text-gray-600">{nextStep.name}</span>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500 italic">All phases completed</p>
                            )}
                          </div>
                          
                          {/* Accountable Person(s) */}
                          <div className="mb-4">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Accountable</h4>
                            {accountablePersons && accountablePersons.length > 0 ? (
                              <div>
                                {accountablePersons.map((person, index) => (
                                  <div key={index} className="flex items-center mb-1">
                                    <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-blue-100">
                                      <span className="text-xs font-medium text-blue-800">A</span>
                                    </span>
                                    <span className="ml-2 text-sm text-gray-600">{person}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500 italic">No accountable person assigned</p>
                            )}
                          </div>
                          
                          {/* Due Date */}
                          <div className="flex items-center text-sm">
                            {dueDateStatus.icon}
                            <span className="ml-1.5 text-sm font-medium" style={{ color: dueDateStatus.color === 'red' ? '#EF4444' : dueDateStatus.color === 'orange' ? '#F59E0B' : dueDateStatus.color === 'green' ? '#10B981' : '#6B7280' }}>
                              {dueDateStatus.text}
                            </span>
                          </div>
                        </div>
                        
                        {/* Card Footer with Actions */}
                        <div className="px-4 py-3 bg-gray-50 flex justify-end space-x-2 border-t border-gray-200">
                          <button
                            onClick={() => addNote('project', project.id)}
                            className="inline-flex items-center p-1.5 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                            title="Add note"
                          >
                            <ChatBubbleLeftIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => navigateTo('project', project.id)}
                            className="inline-flex items-center p-1.5 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                            title="View project"
                          >
                            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default MyWork; 